import { useQuery } from '@tanstack/react-query'
import { getRetirementCandidates } from '../services/retirementApi'
import type { RetirementQuery } from '../types/retirement'
import { SLOW, pollUnlessRefused } from '../utils/polling'

/**
 * One page of retirement candidates.
 *
 * SLOW polling: the signals move when a report runs, a schedule changes or the
 * nightly orphan check flags an owner, so a governance cadence, not a live one.
 *
 * The query is part of the key by value, and the fetch is a closure over the same
 * value, so a filter or page change can never serve the previous filter's page.
 *
 * Takes `enabled` so the page can honour the gate contract (hooks/useLighthouseAdmin.ts):
 * on `denied`, render RestrictedState without fetching an endpoint the caller
 * cannot read; on `unknown`, fetch, and render the endpoint's own 403 as restricted.
 */
export function useRetirementCandidates(
  query: RetirementQuery,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: ['observatory', 'retirement', query],
    queryFn: () => getRetirementCandidates(query),
    enabled,
    refetchInterval: pollUnlessRefused(SLOW),
  })
}
